// Shared helpers for image-frame animations
async function preloadFrameImages(urls) {
  const promises = urls.map(url => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  }));
  return Promise.all(promises);
}

function drawImageFrameOnCanvas(canvas, img, options = {}) {
  if (!canvas || !img) return;
  const ctx = canvas.getContext('2d');

  // canvas logical size in CSS pixels
  const canvasW = canvas.clientWidth;
  const canvasH = canvas.clientHeight;

  // determine maximum allowed draw area (fall back to full canvas)
  const maxWidth = options.drawWidth || canvasW;
  const maxHeight = options.drawHeight || canvasH;

  // source image natural dimensions
  const imgW = img.naturalWidth || img.width || 1;
  const imgH = img.naturalHeight || img.height || 1;
  const imgRatio = imgW / imgH;

  // compute draw size that preserves aspect ratio and fits within max box
  let drawWidth, drawHeight;
  if ((maxWidth / maxHeight) > imgRatio) {
    // constrained by height
    drawHeight = Math.min(maxHeight, canvasH);
    drawWidth = drawHeight * imgRatio;
  } else {
    // constrained by width
    drawWidth = Math.min(maxWidth, canvasW);
    drawHeight = drawWidth / imgRatio;
  }

  // center the image
  const x = (canvasW - drawWidth) / 2;
  const y = (canvasH - drawHeight) / 2;

  // Draw using backing-pixel coordinates to avoid sizing discrepancies
  const backingRatio = (canvas.width / canvas.clientWidth) || 1; // usually devicePixelRatio
  const xBacking = Math.round(x * backingRatio);
  const yBacking = Math.round(y * backingRatio);
  const drawWidthBacking = Math.round(drawWidth * backingRatio);
  const drawHeightBacking = Math.round(drawHeight * backingRatio);

  // preserve current transform, draw in backing pixels, then restore transform
  const t = ctx.getTransform();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, xBacking, yBacking, drawWidthBacking, drawHeightBacking);
  ctx.setTransform(t.a, t.b, t.c, t.d, t.e, t.f);
}

// Play image-frame sequence for any instance that exposes the expected fields
function playImageSequence(instance, targetFrame) {
  return new Promise(resolve => {
    if (targetFrame === instance.currentFrame) { resolve(instance.currentFrame); return; }

    if (instance.frameTimer) {
      clearInterval(instance.frameTimer);
      instance.frameTimer = null;
    }

    instance.isPlayingSequence = true;
    instance.targetFrame = targetFrame;

    instance.frameTimer = setInterval(() => {
      const currentTarget = instance.targetFrame;

      if (instance.currentFrame === currentTarget) {
        clearInterval(instance.frameTimer);
        instance.frameTimer = null;
        instance.isPlayingSequence = false;
        resolve(currentTarget);
        return;
      }

      if (instance.currentFrame < currentTarget) {
        instance.currentFrame++;
      } else {
        instance.currentFrame--;
      }

      if (typeof instance.drawFrame === 'function') instance.drawFrame(instance.currentFrame);
    }, instance.FRAME_INTERVAL_MS);
  });
}

class Ark2000 extends HTMLElement {
  constructor() {
    super();
    this.TOTAL_FRAMES = 31;
    this.FRAMES_PER_SCROLL = 5;
    this.FRAME_INTERVAL_MS = 60;
    this.currentFrame = 0;
    this.isThrottled = false;
    this.isPlayingSequence = false;
    this.isStickyActive = false;
    this.animationComplete = false;
    this.reverseAnimationComplete = false;
    this.targetFrame = 0;
    this.frameTimer = null;
    this.scrollDirection = 0; // 1: 向下, -1: 向上, 0: 无方向
    
    // 线条绘制动画相关变量
    this.isLineDrawing = false;
    this.lineDrawingProgress = 0;
    this.lineDuration = 1000; // 线条绘制持续3秒
    this.lineStartTime = 0;
    this.lineColor = '#fff'; // 线条颜色
    this.lineWidth = 2; // 线条宽度
    this.lineDrawingTimer = null;
    
    // 定义文本内容和位置（基于图片坐标系统）
    this.texts = [];
    // 从port-description脚本中获取文本内容
    
    const portDescScript = document.getElementById('port-description');
    if (portDescScript) {
      const portDescriptions = JSON.parse(portDescScript.textContent);
      this.texts = [
        {
          content: portDescriptions[1] || "USB Output Ports",
          position: { x: -930, y: 410 }, // 图片坐标系中的位置
          align: "right"
        },
        {
          content: portDescriptions[0] || "Cigarette Lighter Port",
          position: { x: -930, y: 170 },
          align: "right"
        },
        {
          content: portDescriptions[2] || "AC Input & PV/CAR Input",
          position: { x: 930, y: 345 }, // 正X值表示从左侧计算
          align: "left"
        },
        {
          content: portDescriptions[3] || "AC Output Ports",
          position: { x: 930, y: 255 },
          align: "left"
        },
        {
          content: portDescriptions[4] || "Extra Battery Ports",
          position: { x: 930, y: 150 },
          align: "left"
        }
      ];
    } 
    
    // 定义五条线
    this.lines = [
      // 第一条线：水平向左 (60, 160)到(-100, 160)
      {
        points: [
          { x: 50, y: 160 },
          { x: -100, y: 160 }
        ]
      },
      // 第二条线：L形 (275, 220)到(275, 400)到(-100, 400)
      {
        points: [
          { x: 275, y: 240 },
          { x: 275, y: 400 },
          { x: -100, y: 400 }
        ]
      },
      // 第三条线：水平向右 (550, 350)到(750, 350)
      {
        points: [
          { x: 670, y: 330 },
          { x: 900, y: 330 }
        ]
      },
      // 第四条线：水平向右 (600, 250)到(750, 250)
      {
        points: [
          { x: 750, y: 240 },
          { x: 900, y: 240 }
        ]
      },
      // 第五条线：水平向右 (550, 60)到(750, 60)
      {
        points: [
          { x: 750, y: 140 },
          { x: 900, y: 140 }
        ]
      }
    ];
    
    this.initialize();
  }
  
  get canvas() {
    return this.querySelector("#animation-canvas");
  }

  get statusPanel() {
    return this.querySelector("#status-panel");
  }

  get images() {
    const urlsScriptElement = document.getElementById('animation-frame-urls');
    const animationFrameUrls = JSON.parse(urlsScriptElement.textContent);
    return animationFrameUrls;
  }
  async initialize() {
    this.setupCanvas();
    this.frameImages = await this.preloadImages();
    
    document.querySelector('.ark2000-animation-container')?.classList?.remove('loading');
    this.statusPanel.classList.add('hidden');
    this.drawFrame(0);
    
    window.addEventListener('scroll', this.handleScroll.bind(this));
    // 使用 passive: false 以确保可以阻止默认滚动行为
    window.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });
    // 添加键盘事件监听，支持上下键控制动画
    window.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  // 设置canvas尺寸
  setupCanvas() {
    const ctx = this.canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    
    const width = window.innerWidth;
    const height = 600;
    
    this.canvas.style.width = width + 'px';
    this.canvas.style.height = height + 'px';
    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;
    ctx.scale(dpr, dpr);
  }

  // 预加载所有图片以确保动画流畅
  preloadImages() {
    const promises = this.images.map(url => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });
    });
    return Promise.all(promises);
  }

  // 获取导航栏高度
  get navbarHeight() {
    const navbar = document.querySelector('.header') || document.querySelector('header') || document.querySelector('[data-header]');
    return navbar ? navbar.offsetHeight : 0;
  }

  // 处理页面滚动事件
  handleScroll() {
    const rect = this.canvas.getBoundingClientRect();
    const navbarHeight = this.navbarHeight;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    // 当 canvas 完整出现在可视区域内时，激活动画截获滚动
    const fullyVisible = rect.top >= navbarHeight && rect.bottom <= viewportHeight;
    this.isStickyActive = fullyVisible;


    // 如果动画正在播放且 sticky 激活，阻止页面滚动
    // if (this.isPlayingSequence && this.isStickyActive) {
    //   // 保存当前滚动位置
    //   const scrollY = window.scrollY || window.pageYOffset;
    //   // 如果页面发生了滚动，立即恢复
    //   requestAnimationFrame(() => {
    //     if (window.scrollY !== scrollY || window.pageYOffset !== scrollY) {
    //       window.scrollTo(0, scrollY);
    //     }
    //   });
    // }
  }

  // 监听滚轮事件来更新帧
  handleWheel(event) {
    // 只有在 sticky 生效时才处理滚轮事件
    if (!this.isStickyActive) return;

    // 检测滚动方向
    const newDirection = event.deltaY > 0 ? 1 : -1;
    const atFirstFrame = this.currentFrame === 0;
    const atLastFrame = this.currentFrame === this.TOTAL_FRAMES - 1;

    // 如果已经在首帧且继续向上，或在末帧且继续向下，则允许正常滚动页面
    if ((newDirection < 0 && atFirstFrame) || (newDirection > 0 && atLastFrame)) {
      return;
    }

    // 动画播放过程中，始终阻止页面滚动
    event.preventDefault();
    
    // 如果正在播放动画，检查是否需要反向
    if (this.isPlayingSequence) {
      // 如果滚动方向改变，立即反向
      if (this.scrollDirection !== newDirection) {
        this.scrollDirection = newDirection;
        // 根据新方向计算目标帧
        if (newDirection > 0) {
          // 向下滚动 - 从当前帧到最后一张
          this.targetFrame = Math.min(this.TOTAL_FRAMES - 1, this.currentFrame + this.FRAMES_PER_SCROLL);
        } else {
          // 向上滚动 - 从当前帧到第一张
          this.targetFrame = Math.max(0, this.currentFrame - this.FRAMES_PER_SCROLL);
        }
        // 不需要重新启动动画，当前动画会自动根据新的 targetFrame 调整方向
        return;
      }
      // 如果方向相同，更新目标帧
      if (newDirection > 0) {
        this.targetFrame = Math.min(this.TOTAL_FRAMES - 1, this.targetFrame + this.FRAMES_PER_SCROLL);
      } else {
        this.targetFrame = Math.max(0, this.targetFrame - this.FRAMES_PER_SCROLL);
      }
      return;
    }

    // 如果不在播放动画，开始新的动画序列
    if (this.isThrottled) return;

    this.isThrottled = true;
    this.scrollDirection = newDirection;

    // 计算目标帧，限制每次滚动的帧数，确保动画流畅
    let targetFrame;
    // 使用固定的帧数增量，而不是基于deltaY的动态增量
    const frameIncrement = event.deltaY > 0 ? 1 : -1;
    
    if (event.deltaY > 0) {
      // 向下滚动 - 每次只前进1帧
      targetFrame = Math.min(this.TOTAL_FRAMES - 1, this.currentFrame + frameIncrement);
    } else {
      // 向上滚动 - 每次只后退1帧
      targetFrame = Math.max(0, this.currentFrame + frameIncrement);
    }

    this.targetFrame = targetFrame;
    this.playFrameSequence(targetFrame);

    setTimeout(() => { this.isThrottled = false; }, 1000);
  }

  // 键盘事件处理，支持上下键控制动画
  handleKeyDown(event) {
    // 只有在 sticky 生效时才处理键盘事件
    if (!this.isStickyActive) return;
    
    // 只处理上下键
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    
    const newDirection = event.key === 'ArrowDown' ? 1 : -1;
    const atFirstFrame = this.currentFrame === 0;
    const atLastFrame = this.currentFrame === this.TOTAL_FRAMES - 1;
    
    // 如果已经在首帧且继续向上，或在末帧且继续向下，则允许正常滚动页面
    if ((newDirection < 0 && atFirstFrame) || (newDirection > 0 && atLastFrame)) {
      // 到达边界，允许默认滚动行为
      return;
    }
    
    // 未到达边界，阻止默认滚动行为
    event.preventDefault();
    
    // 每次按键移动FRAMES_PER_SCROLL帧，与鼠标滚动保持一致
    let targetFrame;
    if (newDirection > 0) {
      targetFrame = Math.min(this.TOTAL_FRAMES - 1, this.currentFrame + this.FRAMES_PER_SCROLL);
    } else {
      targetFrame = Math.max(0, this.currentFrame - this.FRAMES_PER_SCROLL);
    }
    
    this.targetFrame = targetFrame;
    this.playFrameSequence(targetFrame);
  }

  // 播放帧序列
  async playFrameSequence(targetFrame) {
    if (targetFrame === this.currentFrame) return;

    // If the animation is already at the final state (lines/text drawn)
    // and we're about to move to an earlier frame, start the reverse
    // line/text fade-out so they disappear progressively.
    if (this.animationComplete && targetFrame < this.TOTAL_FRAMES - 1 && !this.isLineDrawing) {
      this.startReverseLineDrawingAnimation();
      this.animationComplete = false;
    }

    // use shared image-frame player
    const reached = await playImageSequence(this, targetFrame);

    // after image sequence completes, run frame-end logic (line/text handling stays here)
    if (reached === this.TOTAL_FRAMES - 1) {
      this.animationComplete = true;
      this.startLineDrawingAnimation();
      this.reverseAnimationComplete = false;
    }

    if (reached === 0) {
      this.reverseAnimationComplete = true;
      setTimeout(() => { this.reverseAnimationComplete = false; });
      this.animationComplete = false;
    }
  }
      

  // 启动线条绘制动画
  startLineDrawingAnimation() {
    // 如果动画正在播放，先清除
    if (this.lineDrawingTimer) {
      clearInterval(this.lineDrawingTimer);
      this.lineDrawingTimer = null;
    }
    
    // 重置动画参数
    this.isLineDrawing = true;
    this.lineDrawingProgress = 0;
    this.lineStartTime = Date.now();
    
    // 文本现在在Canvas上绘制，不再需要添加/移除类或定位元素
    // 使用requestAnimationFrame实现平滑动画
    const animateLine = () => {
      // 计算动画进度（0到1之间）
      const elapsed = Date.now() - this.lineStartTime;
      this.lineDrawingProgress = Math.min(1, elapsed / this.lineDuration);
      
      // 重绘当前帧（最后一帧）
      this.drawFrame(this.TOTAL_FRAMES - 1);
      
      // 动画未完成，继续下一帧
      if (this.lineDrawingProgress < 1) {
        this.lineDrawingTimer = requestAnimationFrame(animateLine);
      } else {
        // 动画完成
        this.isLineDrawing = false;
      }
    };
    
    // 启动动画
    this.lineDrawingTimer = requestAnimationFrame(animateLine);
  }

  // 启动反向线条绘制动画（线条逐渐消失）
  startReverseLineDrawingAnimation() {
    // 如果动画正在播放，先清除
    if (this.lineDrawingTimer) {
      cancelAnimationFrame(this.lineDrawingTimer);
      this.lineDrawingTimer = null;
    }
    
    // 重置动画参数
    this.isLineDrawing = true;
    this.lineDrawingProgress = 1; // 从完全绘制状态开始
    this.lineStartTime = Date.now();
    
    // 文本现在在Canvas上绘制，不再需要添加/移除类
    
    // 使用requestAnimationFrame实现平滑动画
    const animateReverseLine = () => {
      // 计算动画进度（1到0之间）
      const elapsed = Date.now() - this.lineStartTime;
      const progress = Math.max(0, 1 - (elapsed / this.lineDuration));
      this.lineDrawingProgress = progress;
      
      // 重绘当前帧（最后一帧）
      this.drawFrame(this.TOTAL_FRAMES - 1);
      
      // 动画未完成，继续下一帧
      if (this.lineDrawingProgress > 0) {
        this.lineDrawingTimer = requestAnimationFrame(animateReverseLine);
      } else {
        // 动画完成
        this.isLineDrawing = false;
      }
    };
    
    // 启动动画
    this.lineDrawingTimer = requestAnimationFrame(animateReverseLine);
  }

  // 绘制指定帧到 Canvas
  drawFrame(frameIndex) {
    const img = this.frameImages[frameIndex];
    if (!img) return;

    const ctx = this.canvas.getContext('2d');
    
    const drawWidth = 800;
    const drawHeight = 535;
    
    const x = (this.canvas.clientWidth - drawWidth) / 2;
    const y = (this.canvas.clientHeight - drawHeight) / 2;

    ctx.clearRect(0, 0, this.canvas.clientWidth, this.canvas.clientHeight);
    ctx.drawImage(img, x, y, drawWidth, drawHeight);
    
    // 绘制线条（以图片左下角为坐标原点）
    if (this.isLineDrawing || this.lineDrawingProgress > 0) {
      // 设置绘制样式
      ctx.strokeStyle = this.lineColor;
      ctx.lineWidth = this.lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // 计算图片左下角在Canvas中的绝对坐标
      const imageBottomLeftX = x;
      const imageBottomLeftY = y + drawHeight;
      
      // 绘制文本
      ctx.font = 'bold 24px Arial';
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      
      this.texts.forEach(text => {
        // 计算文本的实际坐标
        let textX = 0;
        if (text.align === 'left') {
          textX = imageBottomLeftX + text.position.x;
        } else if (text.align === 'right') {
          // 负X值表示从图片右侧计算
          textX = imageBottomLeftX + drawWidth + text.position.x;
        }
        const textY = imageBottomLeftY - text.position.y;
        
        // 设置文本透明度，与线条动画同步
        ctx.globalAlpha = this.lineDrawingProgress;
        
        // 绘制文本 - 支持换行
        ctx.textAlign = text.align;
        
        // 将文本分割成多行，每行不超过430px
        const lineHeight = 30; // 行高为字体大小的1.25倍
        const maxWidth = 430;
        
        if (ctx.measureText(text.content).width <= maxWidth) {
          // 单行文本
          ctx.fillText(text.content, textX, textY);
        } else {
          // 多行文本
          const words = text.content.split(' ');
          let line = '';
          let currentY = textY;
          
          for (let i = 0; i < words.length; i++) {
            const testLine = line + words[i] + ' ';
            const testWidth = ctx.measureText(testLine).width;
            
            if (testWidth > maxWidth && i > 0) {
              // 绘制当前行
              ctx.fillText(line, textX, currentY);
              line = words[i] + ' ';
              currentY += lineHeight;
            } else {
              line = testLine;
            }
          }
          
          // 绘制最后一行
          ctx.fillText(line, textX, currentY);
        }
      });
      
      // 重置透明度
      ctx.globalAlpha = 1;
      
      // 绘制所有线条
      this.lines.forEach(line => {
        // 计算线条的总长度
        let totalLength = 0;
        const segmentLengths = [];
        
        // 计算每段的长度
        for (let i = 0; i < line.points.length - 1; i++) {
          const start = line.points[i];
          const end = line.points[i + 1];
          const dx = end.x - start.x;
          const dy = end.y - start.y;
          const length = Math.sqrt(dx * dx + dy * dy);
          segmentLengths.push(length);
          totalLength += length;
        }
        
        // 计算当前应该绘制的总长度
        const currentDrawLength = totalLength * this.lineDrawingProgress;
        
        // 绘制线条
        ctx.beginPath();
        
        let drawnLength = 0;
        let currentSegmentIndex = 0;
        
        // 计算Canvas中的实际坐标
        const canvasPoints = line.points.map(point => ({
          x: imageBottomLeftX + point.x,
          y: imageBottomLeftY - point.y
        }));
        
        // 在线条起点绘制2px的实心圆
        const startPoint = canvasPoints[0];
        ctx.beginPath();
        ctx.arc(startPoint.x, startPoint.y, 4, 0, Math.PI * 4);
        ctx.fillStyle = this.lineColor;
        ctx.fill();
        
        // 绘制线条
        ctx.beginPath();
        ctx.moveTo(startPoint.x, startPoint.y);
        
        // 绘制每一段
        while (currentSegmentIndex < canvasPoints.length - 1 && drawnLength < currentDrawLength) {
          const segmentStart = canvasPoints[currentSegmentIndex];
          const segmentEnd = canvasPoints[currentSegmentIndex + 1];
          const segmentLength = segmentLengths[currentSegmentIndex];
          
          if (drawnLength + segmentLength <= currentDrawLength) {
            // 绘制整段
            ctx.lineTo(segmentEnd.x, segmentEnd.y);
            drawnLength += segmentLength;
            currentSegmentIndex++;
          } else {
            // 绘制部分段
            const remainingLength = currentDrawLength - drawnLength;
            const segmentProgress = remainingLength / segmentLength;
            
            const currentX = segmentStart.x + (segmentEnd.x - segmentStart.x) * segmentProgress;
            const currentY = segmentStart.y + (segmentEnd.y - segmentStart.y) * segmentProgress;
            
            ctx.lineTo(currentX, currentY);
            break;
          }
        }
        
        ctx.stroke();
      });
    }
  }
}

customElements.define('ark-2000', Ark2000)

// Standalone, image-only Ark2500 element (does NOT extend Ark2000)
class Ark3600 extends HTMLElement {
  constructor() {
    super();
    this.TOTAL_FRAMES = 30;
    this.FRAMES_PER_SCROLL = 10;
    this.FRAME_INTERVAL_MS = 60;
    this.currentFrame = 0;
    this.isThrottled = false;
    this.isPlayingSequence = false;
    this.isStickyActive = false;
    this.targetFrame = 0;
    this.frameTimer = null;
    this.scrollDirection = 0;
    this.prevScrollY = 0;

    this.initialize();
  }

  get canvas() {
    return this.querySelector('#animation-canvas');
  }

  get statusPanel() {
    return this.querySelector('#status-panel');
  }

  // Prefer the script inside this element (safer when multiple instances exist)
  get images() {
    const urlsScriptElement = this.querySelector('#animation-frame-urls') || document.getElementById('animation-frame-urls');
    const animationFrameUrls = JSON.parse(urlsScriptElement.textContent);
    return animationFrameUrls;
  }

  async initialize() {
    this.setupCanvas();
    // use shared preload helper
    this.frameImages = await preloadFrameImages(this.images);
    if (this.statusPanel) this.statusPanel.classList.add('hidden');
    this.drawFrame(0);

    // keep sticky detection on scroll
    window.addEventListener('scroll', this.handleScroll.bind(this));
    // use scroll events to drive frame stepping (do not block page scroll)
    this.prevScrollY = window.scrollY || window.pageYOffset || 0;
    window.addEventListener('scroll', this.handleScrollDrive.bind(this));
  }

  setupCanvas() {
    const ctx = this.canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = 600;
    this.canvas.style.width = width + 'px';
    this.canvas.style.height = height + 'px';
    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;
    ctx.scale(dpr, dpr);
  }

  handleScroll() {
    const rect = this.canvas.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    // When canvas fully visible, enable sticky behavior
    const fullyVisible = rect.top >= 0 && rect.bottom <= viewportHeight;
    this.isStickyActive = fullyVisible;

    // if (this.isPlayingSequence && this.isStickyActive) {
    //   const scrollY = window.scrollY || window.pageYOffset;
    //   requestAnimationFrame(() => {
    //     if (window.scrollY !== scrollY || window.pageYOffset !== scrollY) {
    //       window.scrollTo(0, scrollY);
    //     }
    //   });
    // }
  }

  // Use scroll position changes to step frames (does NOT block page scrolling)
  handleScrollDrive() {
    if (!this.isStickyActive) {
      this.prevScrollY = window.scrollY || window.pageYOffset || 0;
      return;
    }

    const currentY = window.scrollY || window.pageYOffset || 0;
    const delta = currentY - this.prevScrollY;
    this.prevScrollY = currentY;

    // ignore tiny scrolls
    if (Math.abs(delta) < 8) return;

    const newDirection = delta > 0 ? 1 : -1;
    const atFirstFrame = this.currentFrame === 0;
    const atLastFrame = this.currentFrame === this.TOTAL_FRAMES - 1;

    if ((newDirection < 0 && atFirstFrame) || (newDirection > 0 && atLastFrame)) {
      return;
    }

    if (this.isPlayingSequence) return;

    if (this.isThrottled) return;

    this.isThrottled = true;
    this.scrollDirection = newDirection;

    let targetFrame;
    if (newDirection > 0) {
      targetFrame = Math.min(this.TOTAL_FRAMES - 1, this.currentFrame + this.FRAMES_PER_SCROLL);
    } else {
      targetFrame = Math.max(0, this.currentFrame - this.FRAMES_PER_SCROLL);
    }

    this.targetFrame = targetFrame;
    this.playStepSequence(targetFrame);

    setTimeout(() => { this.isThrottled = false; }, 600);
  }

  playStepSequence(targetFrame) {
    return new Promise(resolve => {
      if (targetFrame === this.currentFrame) {
        resolve(this.currentFrame);
        return;
      }

      this.isPlayingSequence = true;
      const step = targetFrame > this.currentFrame ? 1 : -1;
      let framesPlayed = 0;
      const totalFrames = Math.abs(targetFrame - this.currentFrame);

      const animate = () => {
        if (framesPlayed >= totalFrames) {
          this.isPlayingSequence = false;
          resolve(this.currentFrame);
          return;
        }

        this.currentFrame += step;
        framesPlayed++;
        this.drawFrame(this.currentFrame);

        setTimeout(animate, this.FRAME_INTERVAL_MS);
      };

      animate();
    });
  }

  // Draw a single frame using shared helper
  drawFrame(frameIndex) {
    const img = this.frameImages && this.frameImages[frameIndex];
    if (!img) return;
    drawImageFrameOnCanvas(this.canvas, img);
  }
}

customElements.define('ark-3600', Ark3600);

// Gradient Carousel scroll animation controller
class GradientCarousel extends HTMLElement {
  constructor() {
    super();
    this.MAX_TRANSLATE = 100; // max vertical translate in px
    this.OPACITY_THRESHOLD = 0.1; // opacity threshold to start next slide fade-in
  }

  connectedCallback() {
    const isMobile = theme.config.mqlSmall || theme.config.isTouch;
    if (isMobile) return;
    this.wrapper = this.querySelector('.gradient-carousel-wrapper') || document.querySelector('.gradient-carousel-wrapper');
    if (!this.wrapper) return;

    this.slides = Array.from(this.wrapper.querySelectorAll('.gradient-slide'));
    this.totalSlides = this.slides.length;
    if (this.totalSlides === 0) return;

    this.updateAnimation = this.updateAnimation.bind(this);
    window.addEventListener('scroll', this.updateAnimation, { passive: true });
    window.addEventListener('resize', this.updateAnimation);
    this.updateAnimation(); // initialize
  }

  disconnectedCallback() {
    if (this.updateAnimation) {
      window.removeEventListener('scroll', this.updateAnimation);
      window.removeEventListener('resize', this.updateAnimation);
    }
  }

  updateAnimation() {
    const wrapperRect = this.wrapper.getBoundingClientRect();
    const wrapperHeight = this.wrapper.offsetHeight;
    const containerHeight = window.innerHeight;

    // Calculate overall scroll progress (0 to 1)
    const scrollProgress = Math.max(0, Math.min(1, -wrapperRect.top / (wrapperHeight - containerHeight)));
    if (this.totalSlides < 2) return;

    const segmentCount = this.totalSlides - 1;
    const segmentSize = 1 / segmentCount;
    const currentSlideIdx = Math.min(Math.floor(scrollProgress * segmentCount), segmentCount - 1);
    const progressWithin = Math.max(0, Math.min(1, (scrollProgress - currentSlideIdx * segmentSize) / segmentSize));
    const fadeDelay = 0.85; // next slide starts fading in only after current is almost gone

    const currentOpacity = 1 - this._easeInOutQuad(progressWithin);
    const nextOpacity = progressWithin > fadeDelay ? this._easeInOutQuad((progressWithin - fadeDelay) / (1 - fadeDelay)) : 0;

    this.slides.forEach((slide, idx) => {
      let opacity = 0;
      if (idx === currentSlideIdx) {
        opacity = currentOpacity;
      } else if (idx === currentSlideIdx + 1) {
        opacity = nextOpacity;
      }

      const easedOpacity = Math.max(0, Math.min(1, opacity));
      const translateY = -this.MAX_TRANSLATE * (1 - easedOpacity);

      slide.style.opacity = easedOpacity;
      slide.style.willChange = 'opacity, transform';

      const leftEl = slide.querySelector('.left');
      if (leftEl) {
        leftEl.style.transform = `translate(0px, ${translateY}px)`;
      }

      slide.classList.toggle('active', idx === currentSlideIdx);
    });
  }

  _easeInOutQuad(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }
}

customElements.define('gradient-carousel', GradientCarousel);

class logoAnimation extends HTMLElement {
  constructor() {
    super();
    this.canvas = null;
    this.ctx = null;
    this.frameImages = [];
    
    // Animation state and configuration
    this.state = 'fadeInBg';
    this.opacityBg = 0.1;
    this.opacityCards = 0;
    this.lineProgress = 0;
    this.animationFrameId = null;
    this.hasStarted = false;
    this.observer = null;
  }

  async connectedCallback() {
    this.canvas = this.querySelector('#techAnimation');
    if (!this.canvas) return;
    
    this.ctx = this.canvas.getContext('2d');
    
    // Load images from script
    const screenWidth = document.documentElement.clientWidth;
    // 判断是否小于 1024px
    const isLessThan1024 = screenWidth < 1024;
    const urlsScriptElement = isLessThan1024 ? document.getElementById('animation-frame-urls-mobile') : document.getElementById('animation-frame-urls');
    if (!urlsScriptElement) return;
    
    const animationFrameUrls = JSON.parse(urlsScriptElement.textContent);
    
    // Preload all images
    this.frameImages = await Promise.all(
      animationFrameUrls.map(url => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = url;
        });
      })
    );
    
    // Set canvas dimensions to match first image dimensions
    if (this.frameImages.length > 0) {
      const img1 = this.frameImages[0];
      this.canvas.width = img1.width;
      this.canvas.height = img1.height;
    }
    
    // 使用Intersection Observer检测元素是否进入可视区域
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.hasStarted) {
          // 元素进入可视区域，开始动画
          this.hasStarted = true;
          this.render();
          // 动画开始后可以停止观察
          this.observer.unobserve(this);
        }
      });
    }, {
      threshold: 0.1 // 当10%的元素可见时触发
    });
    
    // 开始观察元素
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  // Draw image fitted to canvas dimensions
  drawImageFit(img, x, y, maxWidth, maxHeight) {
    if (!img || !img.width || !img.height) return;
    
    const imgRatio = img.width / img.height;
    const canvasRatio = maxWidth / maxHeight;
    
    let drawWidth, drawHeight, drawX, drawY;
    
    if (imgRatio > canvasRatio) {
      // Image is wider, fit by width
      drawWidth = maxWidth;
      drawHeight = maxWidth / imgRatio;
      drawX = x;
      drawY = y + (maxHeight - drawHeight) / 2;
    } else {
      // Image is taller, fit by height
      drawHeight = maxHeight;
      drawWidth = maxHeight * imgRatio;
      drawX = x + (maxWidth - drawWidth) / 2;
      drawY = y;
    }
    
    this.ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
  }

  // Draw image clipped from center outward
  drawClipImage(img, centerX, centerY, maxRadius, progress) {
    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.arc(centerX, centerY, maxRadius * progress, 0, Math.PI * 2);
    this.ctx.clip();
    
    this.drawImageFit(img, 0, 0, this.canvas.width, this.canvas.height);
    this.ctx.restore();
  }

  // Main render loop
  render = () => {
    if (!this.canvas) return;
    
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    const img1 = this.frameImages[0]; // Background image
    const img2 = this.frameImages[1]; // Cards image
    const img3 = this.frameImages[2]; // Lines image (if needed for line drawing)
    
    if (!img1) {
      this.animationFrameId = requestAnimationFrame(this.render);
      return;
    }

    if (this.state === 'fadeInBg') {
      if (this.opacityBg < .8) this.opacityBg += 0.008;
      else this.state = 'drawLines';
      
      this.ctx.globalAlpha = this.opacityBg;
      this.drawImageFit(img1, 0, 0, this.canvas.width, this.canvas.height);
      this.ctx.globalAlpha = 1;

    } else if (this.state === 'drawLines') {
      this.drawImageFit(img1, 0, 0, this.canvas.width, this.canvas.height);

      if (this.lineProgress < 1) this.lineProgress += 0.01;
      else this.state = 'fadeInCards';

      const maxRadius = Math.sqrt(
        this.canvas.width * this.canvas.width + 
        this.canvas.height * this.canvas.height
      ) / 2;

      if (img3) {
        this.drawClipImage(img3, this.canvas.width / 2, this.canvas.height / 2, maxRadius, this.lineProgress);
      }
      
      if (img2) {
        if (this.lineProgress >= 0.3) {
          const fadeProgress = (this.lineProgress - 0.3) / 0.7;
          this.ctx.globalAlpha = fadeProgress;
          this.drawImageFit(img2, 0, 0, this.canvas.width, this.canvas.height);
          this.ctx.globalAlpha = 1;
        }
      }

    } else if (this.state === 'fadeInCards') {
      this.drawImageFit(img1, 0, 0, this.canvas.width, this.canvas.height);
      
      const maxRadius = Math.sqrt(
        this.canvas.width * this.canvas.width + 
        this.canvas.height * this.canvas.height
      ) / 2;
      
      if (img3) {
        this.drawClipImage(img3, this.canvas.width / 2, this.canvas.height / 2, maxRadius, 1);
      }
      if (img2) {
        
        this.drawImageFit(img2, 0, 0, this.canvas.width, this.canvas.height);
      }
    }

    this.animationFrameId = requestAnimationFrame(this.render);
  };
}

customElements.define('logo-animation', logoAnimation);

// Milestone Show - Interactive Timeline
class MilestoneShow extends HTMLElement {
  constructor() {
    super();
    this.milestones = [];
    this.currentIndex = 0;
    this.isDraggingHandle = false;
    this.isDraggingContent = false;
    this.startX = 0;
    this.currentX = 0;
    this.scrollLeft = 0;
    this.handle = null;
    this.timeline = null;
    this.contentWrapper = null;
    this.markers = [];
    this.contentItems = [];
  }

  connectedCallback() {
    // Get elements
    this.handle = this.querySelector('.timeline-handle');
    this.timeline = this.querySelector('.milestone-timeline');
    this.contentWrapper = this.querySelector('.milestone-content-wrapper');
    this.markers = Array.from(this.querySelectorAll('.timeline-marker'));
    this.contentItems = Array.from(this.querySelectorAll('.milestone-content-item'));
    
    if (!this.handle || !this.timeline || this.markers.length === 0) return;
    
    // Load milestone data
    const dataScript = this.querySelector('script[type="application/json"]');
    if (dataScript) {
      const data = JSON.parse(dataScript.textContent);
      this.milestones = data.milestones || [];
    }
    
    // Initialize positions
    this.updateMarkerPositions();
    this.updateHandlePosition(0);
    this.updateContent(0);
    
    // Add event listeners for timeline handle
    this.handle.addEventListener('mousedown', this.onHandleDragStart.bind(this));
    this.handle.addEventListener('touchstart', this.onHandleDragStart.bind(this), { passive: false });
    document.addEventListener('mousemove', this.onHandleDrag.bind(this));
    document.addEventListener('touchmove', this.onHandleDrag.bind(this), { passive: false });
    document.addEventListener('mouseup', this.onHandleDragEnd.bind(this));
    document.addEventListener('touchend', this.onHandleDragEnd.bind(this));
    
    // Click on markers to jump
    this.markers.forEach((marker, index) => {
      marker.addEventListener('click', () => {
        this.jumpToIndex(index);
      });
    });
    
    // Click on content items to jump
    this.contentItems.forEach((item, index) => {
      item.style.cursor = 'pointer';
      item.addEventListener('click', () => {
        this.jumpToIndex(index);
      });
    });
    
    // Add drag functionality to content wrapper
    this.contentWrapper.addEventListener('mousedown', this.onContentDragStart.bind(this));
    this.contentWrapper.addEventListener('touchstart', this.onContentDragStart.bind(this), { passive: false });
    document.addEventListener('mousemove', this.onContentDrag.bind(this));
    document.addEventListener('touchmove', this.onContentDrag.bind(this), { passive: false });
    document.addEventListener('mouseup', this.onContentDragEnd.bind(this));
    document.addEventListener('touchend', this.onContentDragEnd.bind(this));
    
    // Resize handler
    window.addEventListener('resize', () => {
      this.updateMarkerPositions();
      this.updateHandlePosition(this.currentIndex);
    });
  }

  updateMarkerPositions() {
    const timelineRect = this.timeline.getBoundingClientRect();
    
    const timelineWidth = timelineRect.width;
    const markerCount = this.markers.length;
    
    this.markerPositions = this.markers.map((marker, index) => {
      const percentage = markerCount > 1 ? index / markerCount : 0;
      return percentage * timelineWidth;
    });
  }

  onHandleDragStart(e) {
    this.isDraggingHandle = true;
    this.handle.style.transition = 'none';
    
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    this.startX = clientX;
    
    const timelineRect = this.timeline.getBoundingClientRect();
    this.timelineLeft = timelineRect.left;
    this.timelineWidth = timelineRect.width;
    
    e.preventDefault();
  }

  onHandleDrag(e) {
    if (!this.isDraggingHandle) return;
    
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const relativeX = clientX - this.timelineLeft;
    const clampedX = Math.max(0, Math.min(this.timelineWidth, relativeX));
    
    // Find closest marker
    let closestIndex = 0;
    let minDistance = Infinity;
    
    this.markerPositions.forEach((pos, index) => {
      const distance = Math.abs(clampedX - pos);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });
    
    // Update if changed
    if (closestIndex !== this.currentIndex) {
      this.currentIndex = closestIndex;
      this.updateContent(closestIndex);
    }
    
    // Update handle position in real-time
    const handleOffset = this.handle.offsetWidth / 2;
    this.handle.style.left = `${clampedX - handleOffset}px`;
    
    e.preventDefault();
  }

  onHandleDragEnd(e) {
    if (!this.isDraggingHandle) return;
    
    this.isDraggingHandle = false;
    this.handle.style.transition = 'left 0.3s ease';
    
    // Snap to closest marker
    this.updateHandlePosition(this.currentIndex);
  }

  onContentDragStart(e) {
    this.isDraggingContent = true;
    this.contentWrapper.style.cursor = 'grabbing';
    this.contentWrapper.style.scrollBehavior = 'auto';
    
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    this.startX = clientX;
    this.currentX = clientX;
    this.scrollLeft = this.contentWrapper.scrollLeft;
    
    e.preventDefault();
  }

  onContentDrag(e) {
    if (!this.isDraggingContent) return;
    
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const deltaX = this.currentX - clientX;
    this.contentWrapper.scrollLeft += deltaX;
    this.currentX = clientX;
    
    // Calculate which content item is most visible
    const wrapperRect = this.contentWrapper.getBoundingClientRect();
    const wrapperCenter = wrapperRect.left + wrapperRect.width / 2;
    
    let closestIndex = 0;
    let minDistance = Infinity;
    
    this.contentItems.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();
      const itemCenter = itemRect.left + itemRect.width / 2;
      const distance = Math.abs(wrapperCenter - itemCenter);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });
    
    // Update if changed
    if (closestIndex !== this.currentIndex) {
      this.currentIndex = closestIndex;
      this.updateHandlePosition(closestIndex);
      this.updateContentOpacity(closestIndex);
    }
    
    e.preventDefault();
  }

  onContentDragEnd(e) {
    if (!this.isDraggingContent) return;
    
    this.isDraggingContent = false;
    this.contentWrapper.style.cursor = '';
    this.contentWrapper.style.scrollBehavior = 'smooth';
    
    // Snap to closest content item
    this.updateContent(this.currentIndex);
  }

  jumpToIndex(index) {
    this.currentIndex = index;
    this.updateHandlePosition(index);
    this.updateContent(index);
  }

  updateHandlePosition(index) {
    if (!this.markerPositions || index >= this.markerPositions.length) return;
    
    const position = this.markerPositions[index];
    const handleOffset = this.handle.offsetWidth / 2;
    this.handle.style.left = `${position - handleOffset}px`;
    this.handle.setAttribute('data-current', index);
  }

  updateContent(index) {
    // Update content items opacity
    this.updateContentOpacity(index);
    
    // Scroll content into view if needed
    const activeItem = this.contentItems[index];
    if (activeItem) {
      const itemRect = activeItem.getBoundingClientRect();
      const wrapperRect = this.contentWrapper.getBoundingClientRect();
      
      // Check if item is outside visible area
      if (itemRect.left < wrapperRect.left || itemRect.right > wrapperRect.right) {
        // Calculate scroll position to center the item
        const scrollLeft = activeItem.offsetLeft - (this.contentWrapper.offsetWidth / 2) + (activeItem.offsetWidth / 2);
        this.contentWrapper.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }
  }

  updateContentOpacity(index) {
    // Update content items with active class instead of opacity
    this.contentItems.forEach((item, i) => {
      if (i === index) {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });

    // Update marker year colors
    this.markers.forEach((marker, i) => {
      const yearElement = marker.querySelector('.marker-year');
      if (yearElement) {
        if (i === index) {
          yearElement.classList.add('active');
        } else {
          yearElement.classList.remove('active');
        }
      }
    });
  }
  disconnectedCallback() {
    // Cleanup event listeners
    document.removeEventListener('mousemove', this.onHandleDrag);
    document.removeEventListener('touchmove', this.onHandleDrag);
    document.removeEventListener('mouseup', this.onHandleDragEnd);
    document.removeEventListener('touchend', this.onHandleDragEnd);
    document.removeEventListener('mousemove', this.onContentDrag);
    document.removeEventListener('touchmove', this.onContentDrag);
    document.removeEventListener('mouseup', this.onContentDragEnd);
    document.removeEventListener('touchend', this.onContentDragEnd);
  }
}

customElements.define('milestone-show', MilestoneShow);
